import React, {useEffect, useState} from "react";
import {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {Box, Button, CircularProgress, Dialog, IconButton, Stack, Tab, Tabs} from "@mui/material";
import ReservationProfiles from "../reservationDialog/ReservationProfiles";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import CloseIcon from "@mui/icons-material/Close";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {Availability} from "crqlar-react-common/dist/models/availability/availability.model";
import {GeneralSet} from "crqlar-react-common/dist/utils/generalSet";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {useSnackbar} from 'notistack'
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {AgeCategory, AgeCategoryType} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import ReservationDialog from "../reservationDialog/ReservationDialog";
import ServiceDialogPlannerDetails from "./ServiceDialogPlannerDetails";
import ServiceDialogReservations from "./ServiceDialogReservations";
import PrintIcon from "@mui/icons-material/Print";
import {
    formatDateDMYHM,
    formatDateHMRange,
    formatFullDMY,
    isSameDay
} from "crqlar-react-common/dist/utils/date.util";

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    sector?: Sector;
    resourceCategories: ResourceCategory[];
    markedService: {
        reservations: Reservation[];
        startDate: Date;
        endDate: Date;
        service: Service;
        isException: boolean;
        availability: Availability;
    } | undefined;
    profiles: Profile[];
}

const ServiceDialog = ({
                           open,
                           onClose,
                           onSave,
                           sector,
                           resourceCategories,
                           markedService,
                           profiles,
                       }: Props) => {
    const {t} = useLocalize();
    const {enqueueSnackbar} = useSnackbar();

    const [isLoading, setIsLoading] = useState(false);
    const [tmpService, setTmpService] = useState<Service | undefined>(undefined);
    const [resources, setResources] = useState<Resource[]>([]);
    const [activeTab, setActiveTab] = useState<'planner' | 'reservations'>('planner');
    const [chosenProfile, setChosenProfile] = useState<Profile>();
    const [markedReservation, setMarkedReservation] = useState<Reservation>();
    const [addedProfileIds, setAddedProfileIds] = useState<string[]>([]);
    const [removedProfileIds, setRemovedProfileIds] = useState<string[]>([]);
    const [localReservations, setLocalReservations] = useState<Reservation[]>([]);

    const [ageCategories, setAgeCategories] = useState<AgeCategory[]>([]);
    const [loadedProfiles, setLoadedProfiles] = useState<Profile[]>([]);

    const maxCapacity = markedService?.availability?.capacityOverride ?? 0;

    const loadProfiles = async (profileIds: string[]): Promise<Profile[]> => {
        if (profileIds.length === 0) return [];

        const content: Profile[] = [];
        let hasMore = false;
        let offset = 0;

        do {
            const profilesPage = await ProfileService.getAllProfiles({
                id: profileIds,
                limit: {
                    offset: offset,
                    limit: 1000,
                    sort: [{field: "name", direction: "ASC"}]
                }
            });

            content.push(...(profilesPage.content || []));
            hasMore = !profilesPage.last;
            offset += 1000;
        } while (hasMore);

        return content;
    };

    const loadServiceData = async () => {
        const serviceId = markedService?.service.id;
        const start = markedService?.startDate
        const end = markedService?.endDate

        if (!serviceId || !start || !end) return;

        try {
            const servicesPage = await ServiceService.getAllServices({
                id: [serviceId],
                limit: {
                    offset: 0,
                    limit: 1,
                    sort: [{field: "id", direction: "DESC"}]
                }
            });

            const service = (servicesPage.content || [])[0];
            if (!service) throw new Error("Service not found");
            setTmpService(service);

            const reservationPage = await ReservationService.getAllReservations({
                serviceId: [service.id!],
                limit: {
                    offset: 0,
                    limit: 50,
                    sort: [{field: "id", direction: "DESC"}]
                },
                overlaps: {start: start, end: end}
            });

            const resourceIds = service?.resourceIds ?? [];
            const resourcesPage = ResourceService.getAllResources({
                id: resourceIds,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "name", direction: "ASC"}]
                }
            });

            const reservations = reservationPage.content || [];
            setLocalReservations(reservations);

            const profileIdsFromReservations = reservations
                .flatMap(r => r.profileIds)
                .filter(Boolean) as string[];

            const uniqueProfileIds = Array.from(new Set(profileIdsFromReservations));

            const missingProfileIds = uniqueProfileIds.filter(
                id => !profiles.some(p => p.id === id)
            );

            let newProfiles: Profile[] = [];
            if (missingProfileIds.length > 0) {
                newProfiles = await loadProfiles(missingProfileIds);
            }

            // Merge with existing profiles
            const allLoadedProfiles = [...profiles, ...newProfiles];
            setLoadedProfiles(allLoadedProfiles);

            const serviceAgeCategoryIds = service?.ageCategoryIds ?? [];
            const reservationAgeCategoryIds = reservations
                .flatMap(r => r.guestCounts?.map(g => g.ageCategoryId))
                .filter(Boolean) as string[];
            const ageCategoryIds = new GeneralSet([...reservationAgeCategoryIds, ...serviceAgeCategoryIds]).values();

            const ageCategoryPage = AgeCategoryService.getAllAgeCategories({
                id: ageCategoryIds,
                limit: {
                    offset: 0,
                    limit: 1000,
                    sort: [{field: "name", direction: "ASC"}]
                }
            });

            const [loadedResources, ageCategories] = await Promise.all([
                resourcesPage,
                ageCategoryPage
            ]);

            setAgeCategories(ageCategories.content || []);
            setResources(loadedResources.content || []);
        } catch (err) {
            console.error("Error loading service data:", err);
            enqueueSnackbar(t("Failed to load service"), {
                variant: 'error',
                autoHideDuration: 7000
            });
        }
    };

    useEffect(() => {
        if (!open || !markedService?.service?.id) {
            setTmpService(undefined);
            setAddedProfileIds([]);
            setRemovedProfileIds([]);
            setResources([]);
            setActiveTab("planner");
            setChosenProfile(undefined);
            setLocalReservations([]);
            return;
        }

        setIsLoading(true);
        loadServiceData().finally(() => setIsLoading(false));
    }, [markedService?.service?.id, open]);

    const handleAddProfile = async (profile: Profile) => {
        setAddedProfileIds(prev => [...prev, profile.id!]);

        const newReservation: Reservation = {
            profileIds: [profile.id!],
            serviceId: tmpService?.id,
            resourceIds: tmpService?.resourceIds,
            expectedStartDate: markedService?.startDate,
            expectedEndDate: markedService?.endDate,
            status: ReservationStatus.CONFIRMED,
            sectorId: sector?.id
        };

        const reservation = await ReservationService.createReservation(newReservation);
        setLocalReservations(prev => [reservation, ...prev]);
        setMarkedReservation(reservation);
    };

    const handleRemoveProfile = async (profileId: string) => {
        setMarkedReservation(undefined);
        setRemovedProfileIds(prev => [...prev, profileId]);

        const filtered = localReservations.filter(
            r => !r.profileIds?.includes(profileId)
        );
        setLocalReservations(filtered);
    };

    const handleClose = async () => {
        try {
            const cancelledReservations = (markedService?.reservations ?? [])
                .filter(reservation =>
                    reservation.profileIds?.some(profileId => removedProfileIds.includes(profileId))
                )
                .filter(Boolean) as Reservation[];

            await Promise.all(
                cancelledReservations.map(reservation =>
                    ReservationService.updateReservation({
                        ...reservation,
                        status: ReservationStatus.CANCELLED
                    })
                )
            );

            onSave();
            onClose();
        } catch (err) {
            console.error("Error saving changes:", err);
            enqueueSnackbar(t("Failed to save changes"), {
                variant: 'error',
                autoHideDuration: 7000
            });
        }
    };

    const getServiceProfileIds = (): string[] => {
        const reservations = markedService?.reservations ?? [];
        return [
            ...reservations.flatMap(p => p.profileIds),
            ...addedProfileIds
        ]
            .filter(id => !removedProfileIds.includes(id!))
            .filter(Boolean) as string[];
    };


    const handleSaveReservation = async (reservation: Reservation | undefined) => {
        if (!reservation) return;
        const saved = await ReservationService.updateReservation(reservation);
        const updated = localReservations.map(r => r.id === reservation.id ? saved : r);
        setLocalReservations(updated);
        setMarkedReservation(undefined);
    };


    return (
        <>
            <ReservationDialog
                open={!!markedReservation}
                onClose={() => setMarkedReservation(undefined)}
                reservation={markedReservation}
                onSave={handleSaveReservation}
                onChangeReservation={setMarkedReservation}
            />

            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth={'lg'}
            >
                <style>{`
                    @media print {
                        @page { size: A4 portrait; margin: 15mm; }
                        body > #root,
                        body > iframe { display: none !important; }
                        .MuiBackdrop-root { display: none !important; }
                        .MuiDialog-container {
                            height: auto !important;
                            display: block !important;
                            position: static !important;
                        }
                        .MuiDialog-paper {
                            max-width: none !important;
                            width: 100% !important;
                            margin: 0 !important;
                            box-shadow: none !important;
                            overflow: visible !important;
                            max-height: none !important;
                            position: static !important;
                        }
                        .service-dialog-hide-on-print { display: none !important; }
                        .service-dialog-actions-cell { display: none !important; }
                        .service-dialog-print-zone {
                            max-height: none !important;
                            overflow: visible !important;
                            margin-top: 0 !important;
                        }
                        .service-dialog-print-zone tr { page-break-inside: avoid; }
                        .service-dialog-print-zone .MuiTableCell-root {
                            padding-top: 4px !important;
                            padding-bottom: 4px !important;
                        }
                        * {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                            color-adjust: exact;
                        }
                    }
                `}</style>
                <Stack direction="row" width="100%">
                    {isLoading ? (
                        <Box className="service-dialog-hide-on-print" sx={{
                            width: "400px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <CircularProgress size={24}/>
                        </Box>
                    ) : (
                        <Box className="service-dialog-hide-on-print">
                            <ReservationProfiles
                                open={open}
                                expandOnMount={false}
                                maxAllowedCount={maxCapacity}
                                serviceProfileIds={getServiceProfileIds()}
                                addProfile={handleAddProfile}
                                removeProfileId={handleRemoveProfile}
                                fullProfilePreview={chosenProfile}
                                setFullProfilePreview={setChosenProfile}
                                onCreateProfile={handleAddProfile}
                                reservation={markedReservation}
                                setReservation={setMarkedReservation}
                                onClose={handleClose}
                                closeAccordionOnChange
                            />
                        </Box>
                    )}

                    {!chosenProfile && (
                        <Stack sx={{flex: 1, p: 2, minHeight: "660px"}}>
                            <Stack
                                className="service-dialog-hide-on-print"
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                width="100%"
                            >
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, value: 'planner' | 'reservations') => setActiveTab(value)}
                                >
                                    <Tab value="planner"
                                         label={t('Planner details')}/>
                                    <Tab
                                        value="reservations"
                                        label={t('Reservations') + (localReservations.length > 0 ? ` (${localReservations.length})` : '')}
                                    />
                                </Tabs>

                                <Stack flexDirection={"row"} gap={2} alignItems={"center"}>
                                    {activeTab === "reservations" && <>
                                        <Box>
                                            <Button
                                                startIcon={<PrintIcon/>}
                                                variant="outlined"
                                                size={"small"}
                                                onClick={() => window.print()}
                                            >
                                                {t('Print')}
                                            </Button>
                                        </Box>
                                    </>}
                                    <Box>
                                        <IconButton onClick={handleClose}>
                                            <CloseIcon/>
                                        </IconButton>
                                    </Box>
                                </Stack>
                            </Stack>

                            {activeTab === 'planner' &&
                                <Box sx={{mt: 4}}>

                                    {markedService && tmpService &&
                                        <ServiceDialogPlannerDetails
                                            localReservations={localReservations}
                                            ageCategories={ageCategories}
                                            tmpService={tmpService}
                                            markedService={markedService}
                                            maxCapacity={maxCapacity}
                                            resources={resources}
                                            resourceCategories={resourceCategories}
                                        />}

                                </Box>}
                            {activeTab === 'reservations' &&
                                <Box
                                    className="service-dialog-print-zone"
                                    sx={{mt: 3, overflow: "auto", maxHeight: "600px"}}
                                >
                                    <Box sx={{
                                        display: 'none',
                                        '@media print': {display: 'block', mb: 3}
                                    }}>
                                        <Box sx={{fontSize: '18pt', fontWeight: 'bold'}}>
                                            {tmpService?.name || markedService?.service?.name || t('Reservations')}
                                        </Box>
                                        {markedService?.startDate && markedService?.endDate && (
                                            <Box sx={{fontSize: '11pt', color: '#444'}}>
                                                {isSameDay(markedService.startDate, markedService.endDate)
                                                    ? `${formatFullDMY(markedService.startDate)} · ${formatDateHMRange(markedService.startDate, markedService.endDate)}`
                                                    : `${formatDateDMYHM(markedService.startDate)} – ${formatDateDMYHM(markedService.endDate)}`}
                                            </Box>
                                        )}
                                        <Box sx={{fontSize: '10pt', color: '#666', mt: 0.5}}>
                                            {(() => {
                                                const adultIds = new Set(ageCategories.filter(ac => ac.type === AgeCategoryType.ADULT).map(ac => ac.id));
                                                let total = 0, adults = 0;
                                                for (const r of localReservations) {
                                                    for (const gc of r.guestCounts ?? []) {
                                                        const n = gc.count || 0;
                                                        total += n;
                                                        if (adultIds.has(gc.ageCategoryId)) adults += n;
                                                    }
                                                }
                                                const nonAdults = total - adults;
                                                return `${localReservations.length} ${t('Reservations')} · ${total} ${t('Guests')} (${adults}/${nonAdults})`;
                                            })()}
                                        </Box>
                                    </Box>

                                    <ServiceDialogReservations
                                        ageCategories={ageCategories}
                                        localReservations={localReservations}
                                        loadedProfiles={loadedProfiles}
                                        setLocalReservations={setLocalReservations}
                                        setMarkedReservation={setMarkedReservation}
                                    />
                                </Box>}
                        </Stack>
                    )}
                </Stack>
            </Dialog>


        </>
    );
};

export default ServiceDialog;
